"use client";

export type WorkTimerNotification = {
  jobId: string;
  status: "running" | "paused";
  startedAt: string | null;
  lastStartedAt: string | null;
  accumulatedMs: number;
  jobTitle?: string | null;
};

const swPath = "/sw.js";

function notificationTag(jobId: string) {
  return `work-timer-${jobId}`;
}

function notificationStorageKey(jobId: string) {
  return `work-timer-notification:${jobId}`;
}

function notificationSignature(timer: WorkTimerNotification) {
  const pauseStamp = timer.status === "paused" ? Math.round(timer.accumulatedMs / 60000) : 0;
  return [timer.jobId, timer.status, timer.startedAt ?? "", pauseStamp, timer.jobTitle ?? ""].join("|");
}

function canUseNotifications() {
  return typeof window !== "undefined"
    && "Notification" in window
    && "serviceWorker" in navigator
    && (window.isSecureContext || window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
}

export async function registerTimerServiceWorker() {
  if (!canUseNotifications()) return null;
  try {
    const registration = await navigator.serviceWorker.register(swPath);
    void registration.update();
    return registration;
  } catch {
    return null;
  }
}

export async function requestTimerNotificationPermission() {
  if (!canUseNotifications()) return "unsupported" as const;
  await registerTimerServiceWorker();
  if (Notification.permission === "granted") return "granted" as const;
  if (Notification.permission === "denied") return "denied" as const;
  return await Notification.requestPermission();
}

export async function closeWorkTimerNotification(jobId: string) {
  if (!canUseNotifications() || Notification.permission !== "granted") return;
  const registration = await registerTimerServiceWorker();
  if (!registration?.getNotifications) return;
  const notifications = await registration.getNotifications({ tag: notificationTag(jobId) });
  notifications.forEach((notification) => notification.close());
  window.localStorage.removeItem(notificationStorageKey(jobId));
}

export async function syncWorkTimerNotifications(timers: WorkTimerNotification[]) {
  if (!canUseNotifications() || Notification.permission !== "granted") return;
  const registration = await registerTimerServiceWorker();
  if (!registration) return;

  const currentTags = new Set(timers.map((timer) => notificationTag(timer.jobId)));
  const existingByTag = new Map<string, Notification>();
  if (registration.getNotifications) {
    const existing = await registration.getNotifications();
    existing
      .filter((notification) => notification.tag.startsWith("work-timer-") && !currentTags.has(notification.tag))
      .forEach((notification) => notification.close());
    existing
      .filter((notification) => notification.tag.startsWith("work-timer-") && currentTags.has(notification.tag))
      .forEach((notification) => existingByTag.set(notification.tag, notification));
  }

  for (const timer of timers) {
    const title = timer.jobTitle || "Auftrag";
    const running = timer.status === "running";
    const tag = notificationTag(timer.jobId);
    const signature = notificationSignature(timer);
    const existingNotification = existingByTag.get(tag);
    const existingStatus = (existingNotification?.data as any)?.status;
    if (existingNotification && existingStatus === timer.status) continue;
    if (!existingNotification && window.localStorage.getItem(notificationStorageKey(timer.jobId)) === signature) continue;
    existingNotification?.close();

    const options = {
      tag,
      body: `${title}${running ? "" : " - pausiert"}`,
      icon: "/icon-192.png",
      badge: "/icon-192.png",
      requireInteraction: true,
      renotify: false,
      silent: true,
      data: {
        ...timer,
        boardUrl: `/schedule/board?jobId=${encodeURIComponent(timer.jobId)}&openJob=1`,
        homeUrl: `/?timerJobId=${encodeURIComponent(timer.jobId)}&timerAction=stop`,
      },
      actions: running
        ? [
            { action: "pause", title: "Pause" },
            { action: "stop", title: "Buchen" },
          ]
        : [
            { action: "resume", title: "Start" },
            { action: "stop", title: "Buchen" },
          ],
    } as NotificationOptions & { actions?: { action: string; title: string }[] };

    await registration.showNotification("Offener Auftrag", options);
    window.localStorage.setItem(notificationStorageKey(timer.jobId), signature);
  }
}
